import React from "react";
import { Button } from "../../button/Button";
import { Card } from "../../Card";
import { OnOff } from "../../inputs/OnOff";
import { TextInput } from "../../inputs/TextInput";
import { CardPager } from "../CardPager";
import { Flex, FlexItem } from "../FlexHelpers";

export type ItemSelectorItem = {
    selectionValue: string,
    selected: boolean,
    searchTexts: string[],
    renderComponent: React.ReactNode;
};

type ItemSelectorPanelProps = {
    pageSize: number,
    searchPlaceholder: string,
    noItemsElement: React.ReactNode,
    items: ItemSelectorItem[],
    selectMode?: 'onOff' | 'button-add-remove' | 'button-select',
    itemListMinHeight?:string,
    onSelectionChanged?: (selectionValue: string, checked: boolean) => void,
    onSaveChanges?: () => void, // Optional save changes function
    onCancel?: () => void; // Optional cancel function
};
export function ItemSelectorPanel (props: ItemSelectorPanelProps): JSX.Element
{
    const [searchText, setSearchText] = React.useState("");
    const [pageIndex, setPageIndex] = React.useState(0);

    function handleFilterChange (newFilter: string)
    {
        setSearchText(newFilter);
        setPageIndex(0);
    }

    const filteredItems = searchText === ''
        ? props.items
        : props.items.filter(i => i.searchTexts.some(t => t.toLowerCase().includes(searchText.toLowerCase())));

    const offset = pageIndex * props.pageSize;
    const pageItems = filteredItems.slice(offset, offset + props.pageSize);

    const mode = props.selectMode || 'onOff';

    return (
        <Flex flexDirection="column" gap={10} justifyContent="space-between">

            <Flex flexDirection="column" gap={10}>

                <Flex alignItems="center" gap={4}>
                    <FlexItem flexGrow={"1"}>
                        <TextInput
                            value={searchText}
                            onChange={handleFilterChange}
                            placeholder={props.searchPlaceholder} />
                    </FlexItem>
                    <Button variant="text" icon='actions_close' onClick={() => handleFilterChange('')} />
                </Flex>

                <Flex flexDirection="column" gap={4} style={{ minHeight: props.itemListMinHeight || (props.pageSize * 64) + 'px' }}>
                    {props.items.length === 0 &&
                        <>
                            {props.noItemsElement}
                        </>
                    }

                    {props.items.length > 0 && filteredItems.length === 0 &&
                        <div>
                            Ingen resultater for søgningen...
                        </div>
                    }
                    {pageItems.map(u =>
                    {
                        return (
                            <Card key={u.selectionValue} variant="outlined" padding="4px">
                                <Flex key={u.selectionValue} alignItems="center">
                                    <div style={{ flexGrow: 1 }}>
                                        {u.renderComponent}
                                    </div>
                                    <Flex>
                                        {mode === 'onOff' && (
                                            u.selected ?
                                                <OnOff active={u.selected} size="large" onClick={() => props.onSelectionChanged?.(u.selectionValue, false)} />
                                                :
                                                <OnOff active={u.selected} size="large" onClick={() => props.onSelectionChanged?.(u.selectionValue, true)} />
                                        )}
                                        {(mode === 'button-add-remove' || mode === 'button-select') && (
                                            <Button
                                                text={
                                                    mode === 'button-select'
                                                        ? 'Vælg'
                                                        : (u.selected ? "Fjern" : "Tilføj")
                                                }
                                                variant="text"
                                                onClick={() => props.onSelectionChanged?.(u.selectionValue, !u.selected)} />
                                        )}

                                    </Flex>
                                </Flex>
                            </Card>
                        );
                    })}
                    {(props.onSaveChanges || props.onCancel) &&
                        <Flex justifyContent="flex-end" style={{ marginTop: 10 }} gap={8}>
                            {props.onCancel &&
                                <Button
                                    text="Annuller"
                                    variant="text"
                                    onClick={props.onCancel} />
                            }
                            {props.onSaveChanges &&
                                <Button
                                    text="Gem"
                                    variant="filled"
                                    onClick={props.onSaveChanges} />
                            }</Flex>
                    }
                </Flex>
            </Flex>

            <CardPager
                currentPage={pageIndex + 1}
                items={filteredItems.length}
                itemsPerPage={props.pageSize}
                onPageChange={(newPage) => setPageIndex(newPage - 1)} />
        </Flex>
    );
}